import * as iconData from '@maui/icon-data'
import { fetchOverride } from '@maui-mf/common-auth'
import { registerApplication, start as startSingleSpa, triggerAppChange } from 'single-spa'
import './trackers'
import * as authFailure from './auth-failure'
import * as gti from './gti'
import * as replaceTrackingParams from './replace-tracking-params'
import * as featureFlags from './feature-flags'
import { buildRoutes } from './routes'
import { parsePackageSpecifier } from './utils/parsePackageSpecifier'
import { ready as authReady } from './auth-store'
import { AppRoute } from './AppRoute'
import * as vtRedirects from './vt-redirects'
import env from '@maui-mf/env'

// TODO dashboards organization has a lot of issues because of the hasty extraction of
// @mandiant-mfe/dashboards out of what was then a single app. Here we need to preload this because
// it contains context and it's not a singleton. It's not a great solution, we need to refactor
// maui-mf/app-dashboards, mandiant-mfe/dashboards, and the maui/dashboards feature package.
// It's likely that the mfe dashboards package will no longer exist and it'll just be up to the
// root app to instantiate the provider.
import '@maui/dashboards'

// NOTE we're injecting styles instead of importing from @maui/elements-global to keep the single
// interface to the maui elements bundle through the remote. It'd probably be possible to export
// CSS as a remote (maybe?) but it's not currently set up that way.
import { injectGlobalStyles } from '@maui/elements'
import './styles.css'
injectGlobalStyles()

// Workaround for bug in single-spa-layout which is attempting some kind of cjs/esm interop & failing.
// Specifically it will throw a TypeError complaining about reading pathToActiveWhen of undefined, which
// you can see is happening incorrectly in the "fixed" source.
//
// By `require`ing we force the import of the CJS version via the package.json `exports` mapping. Note
// this is the only reasonable way to import this file as the package.json's exports preclude otherwise
// importing dist.
//
// See: https://github.com/single-spa/single-spa-layout/issues/93
const { constructApplications, constructRoutes, constructLayoutEngine } = require('single-spa-layout')

window.__maui_icon_data__ = iconData
window.__maui_icon_prefer_font__ = true

const moduleIsContainer = module => {
  return Object.keys(module).length === 2 &&
    typeof module.get === 'function' &&
    typeof module.init === 'function'
}

const shareScope = 'default'

const splitAppRoutes = app => {
  const splits = app.splits || []

  return [...splits, app].map(({ package_name: name, split }) => {
    return new AppRoute({
      type: 'route',
      exact: !!app.exact,
      path: app.path,
      props: {
        ...app.props,
        __meta: {
          ...parsePackageSpecifier(name),
          ...(app.splits && {
            split,
            splits: splits.map(s => s.split),
          })
        }
      },
      routes: [
        { type: 'application', name }
      ],
    })
  })
}

const createRoutes = (appRoutes, variant = 'default') => {
  const splitRoutes = appRoutes.reduce((config, app) => {
    const name = app.package_name

    if (!app.path && app.splits) {
      throw new Error('splits are only supported non-default path routed apps')
    }

    if (app.default) {
      config.routed.push({
        type: 'route',
        default: true,
        routes: [
          { type: 'application', name }
        ],
      })
    } else if (app.path) {
      config.routed.push(...splitAppRoutes(app))
    } else {
      config.unrouted.push({
        type: 'application',
        name,
      })
    }

    return config
  }, {
    routed: [],
    unrouted: [],
  })

  return constructRoutes({
    routes: buildRoutes(variant, splitRoutes)
  })
}

const loadApp = async ({ name }) => {
  const { entryPath, packageName } = parsePackageSpecifier(name)

  const mod = await System.import(packageName)

  if (!moduleIsContainer(mod)) {
    return mod
  }

  await __webpack_init_sharing__(shareScope)
  await mod.init(__webpack_share_scopes__[shareScope])
  const factory = await mod.get(entryPath)
  return factory()
}

const createApplications = (routes) => {
  const applications = constructApplications({ routes, loadApp })
  return applications
}

export const start = async (options = {}) => {
  const store = await authReady()
  const storeValue = store.getValue()

  fetchOverride.init()

  await featureFlags.start()
  await authFailure.start()
  await replaceTrackingParams.start()
  await vtRedirects.start(env, storeValue?.isGTI)

  const uxVariant = gti.shouldUseLayout(storeValue) ? 'gti' : 'default'
  const routes = createRoutes(options.routes, uxVariant)
  const applications = createApplications(routes)

  applications.forEach(registerApplication)
  constructLayoutEngine({ applications, routes })
  startSingleSpa()

  store.subscribe(() => {
    triggerAppChange()
  })
}
